import { useMemo, useState } from 'react';
import { Check, Loader2, Plus, UserCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { twMerge } from 'tailwind-merge';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import RightDrawer from '../../../../shared/components/form/RightDrawer';
import epcProjectService, {
  type EpcProjectMembershipRow,
  type EpcProjectOrgUnit,
} from '../../../../services/epcProjectService';
import epcDepartmentMasterService, { masterId } from '../../../../services/epcDepartmentMasterService';
import type { User as AppUser } from '../../../../types/user';
import { resolveDefaultGovernanceApprover } from './defaultGovernanceApprover';
import { EpcTeamUserPicker } from './EpcTeamUserPicker';
import { ROLE_LABEL } from './teamCapabilities';
import { resolveDeptPresentation } from './departmentTemplates';
import {
  buildCreateSpecs,
  computeMasterAvailability,
  parseFocusAreas,
  partitionCreateOutcomes,
  toggleTemplateSelection,
  type DeptCreateOutcome,
} from './departmentDrawerSelection';

export type CreatedDept = { masterId: string; name: string; orgUnitId: string };

export function EpcDeptMultiSelectDrawer({
  open,
  onClose,
  projectId,
  orgUnits,
  memberships,
  headCandidates,
  onMutated,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  orgUnits: EpcProjectOrgUnit[];
  memberships: EpcProjectMembershipRow[];
  headCandidates: { users: AppUser[]; roleLabelByUserId: Map<string, string | undefined> };
  /** Reload org units + memberships in the parent after any successful write. */
  onMutated: () => Promise<void>;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [customName, setCustomName] = useState('');
  const [customFocus, setCustomFocus] = useState('');
  const [creating, setCreating] = useState(false);
  const [addingToMaster, setAddingToMaster] = useState(false);
  const [errorByKey, setErrorByKey] = useState<Record<string, string>>({});

  type AssignRowState = {
    userId: string;
    status: 'idle' | 'saving' | 'done' | 'skipped' | 'error';
    error?: string;
  };
  const [step, setStep] = useState<'pick' | 'assign'>('pick');
  const [createdDepts, setCreatedDepts] = useState<CreatedDept[]>([]);
  const [assignByOrgId, setAssignByOrgId] = useState<Record<string, AssignRowState>>({});
  const approver = useMemo(() => resolveDefaultGovernanceApprover(memberships), [memberships]);

  const queryClient = useQueryClient();
  const { data: masters = [], isLoading: mastersLoading } = useQuery({
    queryKey: ['epc-department-masters'],
    queryFn: () => epcDepartmentMasterService.list(),
    enabled: open,
    staleTime: 60_000,
  });

  const availability = useMemo(() => computeMasterAvailability(masters, orgUnits), [masters, orgUnits]);
  const blockedIds = useMemo(
    () => new Set(availability.filter((a) => a.alreadyAdded).map((a) => masterId(a.master))),
    [availability],
  );
  /** Drop any selected id that became "already added" since selection (e.g. after a reload). */
  const effectiveSelected = useMemo(
    () => new Set([...selected].filter((id) => !blockedIds.has(id))),
    [selected, blockedIds],
  );
  const specs = useMemo(() => buildCreateSpecs(effectiveSelected, masters), [effectiveSelected, masters]);

  const handleClose = () => {
    setSelected(new Set());
    setCustomName('');
    setCustomFocus('');
    setErrorByKey({});
    setCreating(false);
    setStep('pick');
    setCreatedDepts([]);
    setAssignByOrgId({});
    onClose();
  };

  const handleAddToMaster = async () => {
    const name = customName.trim();
    if (!name || addingToMaster) return;
    setAddingToMaster(true);
    try {
      const focus = parseFocusAreas(customFocus);
      const { id, existed } = await epcDepartmentMasterService.create({
        name,
        ...(focus.length ? { focusAreas: focus } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: ['epc-department-masters'] });
      if (blockedIds.has(id)) {
        toast(existed ? 'That department already exists and is already in this project.' : 'Department added.');
      } else {
        setSelected((prev) => new Set(prev).add(id));
        toast.success(
          existed
            ? 'Department already existed in master — selected it for you.'
            : `"${name}" added to master and selected.`,
        );
      }
      setCustomName('');
      setCustomFocus('');
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Could not add department to master');
    } finally {
      setAddingToMaster(false);
    }
  };

  const handleCreate = async () => {
    if (!projectId || specs.length === 0 || creating) return;
    setCreating(true);
    const outcomes: DeptCreateOutcome[] = [];
    for (const spec of specs) {
      try {
        const ou = await epcProjectService.createOrgUnit(projectId, {
          epcDepartmentMasterId: spec.masterId,
          ...(spec.focusAreas.length ? { focusAreas: spec.focusAreas } : {}),
        });
        const r = ou as { id?: string; _id?: string };
        outcomes.push({ spec, ok: true, orgUnitId: String(r.id ?? r._id ?? '') });
      } catch (err) {
        outcomes.push({
          spec,
          ok: false,
          error: err instanceof Error && err.message ? err.message : 'Could not create department',
        });
      }
    }
    const { created, failed } = partitionCreateOutcomes(outcomes);
    try {
      if (created.length) {
        await onMutated();
        setCreatedDepts((prev) => [...prev, ...created]);
        /* usageCounts changed on the master list. */
        void queryClient.invalidateQueries({ queryKey: ['epc-department-masters'] });
      }
      if (failed.length) {
        /* Partial failure (spec §7): drawer stays open, failed departments stay
           selected with per-item errors; succeeded ones leave the selection. */
        setSelected(new Set(failed.map((f) => f.masterId)));
        setErrorByKey(Object.fromEntries(failed.map((f) => [f.masterId, f.error])));
        toast.error(`${failed.length} department${failed.length === 1 ? '' : 's'} could not be created`);
        return;
      }
      setErrorByKey({});
      toast.success(`${created.length} department${created.length === 1 ? '' : 's'} created`);
      setStep('assign');
    } finally {
      setCreating(false);
    }
  };

  const assignHead = async (dept: CreatedDept) => {
    const pickedUserId = assignByOrgId[dept.orgUnitId]?.userId ?? '';
    if (!pickedUserId || !approver) return;
    setAssignByOrgId((prev) => ({ ...prev, [dept.orgUnitId]: { userId: pickedUserId, status: 'saving' } }));
    try {
      await epcProjectService.createMembership(projectId, {
        userId: pickedUserId,
        role: 'department_head',
        orgUnitId: dept.orgUnitId,
        reportsToUserId: approver.userId,
      });
      setAssignByOrgId((prev) => ({ ...prev, [dept.orgUnitId]: { userId: pickedUserId, status: 'done' } }));
      await onMutated();
    } catch (err) {
      setAssignByOrgId((prev) => ({
        ...prev,
        [dept.orgUnitId]: {
          userId: pickedUserId,
          status: 'error',
          error: err instanceof Error && err.message ? err.message : 'Could not assign head',
        },
      }));
    }
  };

  const skipDept = (dept: CreatedDept) =>
    setAssignByOrgId((prev) => ({ ...prev, [dept.orgUnitId]: { userId: '', status: 'skipped' } }));

  const assignedCount = createdDepts.filter((d) => assignByOrgId[d.orgUnitId]?.status === 'done').length;
  const skippedCount = createdDepts.filter((d) => assignByOrgId[d.orgUnitId]?.status === 'skipped').length;

  return (
    <RightDrawer
      open={open}
      onClose={handleClose}
      title="Add departments"
      size="60"
      footer={
        step === 'pick' ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[rgba(11,59,46,0.12)] bg-[#F8F2E2] px-6 py-3.5">
          <p className="text-[11px] text-[#2A1F17]">
            {specs.length ? (
              <>
                Creating <b className="text-[#1F5A47]">{specs.length} department{specs.length === 1 ? '' : 's'}</b>
                {' · '}
                {specs.slice(0, 3).map((s) => s.name).join(', ')}
                {specs.length > 3 ? ` +${specs.length - 3} more` : ''} — heads can be assigned next
              </>
            ) : (
              'Select departments from the master, or add a new one.'
            )}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 py-1.5 text-[11.5px] font-semibold text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={creating || specs.length === 0}
              onClick={() => void handleCreate()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[rgba(11,59,46,0.45)] bg-[#264f3c] px-3.5 py-1.5 text-[11.5px] font-semibold text-white shadow-[0_8px_18px_-12px_rgba(38,79,60,0.5),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#2d5c47] disabled:opacity-50"
            >
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Create {specs.length} department{specs.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
        ) : (
          <div className="flex items-center justify-between gap-3 border-t border-[rgba(11,59,46,0.12)] bg-[#F8F2E2] px-6 py-3.5">
            <p className="text-[11px] text-[#2A1F17]">
              {assignedCount} assigned · {skippedCount} skipped ·{' '}
              {createdDepts.length - assignedCount - skippedCount} pending
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-[rgba(11,59,46,0.45)] bg-[#264f3c] px-4 py-1.5 text-[11.5px] font-semibold text-white shadow-[0_8px_18px_-12px_rgba(38,79,60,0.5),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#2d5c47]"
            >
              Done
            </button>
          </div>
        )
      }
    >
      {step === 'pick' ? (
        <>
      <p className="mb-4 text-[11.5px] text-[#7C8A82]">
        Select departments from the master, or add a new one. Focus areas drive schedule
        auto-matching.
      </p>

      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#2A1F17]">
        Departments from master · {effectiveSelected.size} selected
      </p>
      {mastersLoading ? (
        <div className="mb-4 flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-[#1F5A47]" />
        </div>
      ) : (
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {availability.map(({ master: m, alreadyAdded }) => {
          const id = masterId(m);
          const Icon = resolveDeptPresentation(m.name).icon;
          const focusAreas = m.focusAreas ?? [];
          const isSelected = effectiveSelected.has(id);
          return (
            <button
              key={id}
              type="button"
              disabled={alreadyAdded || creating}
              aria-pressed={isSelected}
              onClick={() => {
                setSelected((prev) => toggleTemplateSelection(prev, id));
                setErrorByKey((prev) => {
                  const { [id]: _omit, ...rest } = prev;
                  return rest;
                });
              }}
              className={twMerge(
                'relative rounded-[10px] border border-[rgba(11,59,46,0.18)] bg-[rgba(255,254,251,0.96)] px-2.5 py-2 text-left transition',
                isSelected ? 'border-[#1F5A47] bg-[#F8F2E2] shadow-[0_0_0_1px_#1F5A47]' : 'hover:border-[rgba(51,146,116,0.6)]',
                alreadyAdded ? 'cursor-not-allowed bg-[#F2EADB] opacity-60' : 'cursor-pointer',
              )}
            >
              {!alreadyAdded ? (
                <span
                  className={twMerge(
                    'absolute right-2 top-2 flex h-[13px] w-[13px] items-center justify-center rounded border-[1.5px]',
                    isSelected ? 'border-[#1F5A47] bg-[#1F5A47]' : 'border-[rgba(11,59,46,0.28)] bg-[#fcfaf3]',
                  )}
                  aria-hidden
                >
                  {isSelected ? <Check className="h-2.5 w-2.5 text-white" /> : null}
                </span>
              ) : null}
              <span className="mb-1.5 flex h-[22px] w-[22px] items-center justify-center rounded-md border border-[rgba(11,59,46,0.20)] bg-[rgba(201,169,97,0.14)] text-[#0B3B2E]">
                <Icon className="h-3 w-3" />
              </span>
              <span className="block text-[11.5px] font-bold text-[#0B3B2E]">{m.name}</span>
              {alreadyAdded ? (
                <span className="mt-1 inline-block rounded bg-[rgba(201,169,97,0.16)] px-1.5 py-px text-[9px] font-bold text-[#8A5A08]">
                  Already added
                </span>
              ) : (
                <span className="mt-1 flex flex-wrap gap-0.5">
                  {focusAreas.slice(0, 3).map((fa) => (
                    <i key={fa} className="rounded bg-[#F8F2E2] px-1 text-[9px] not-italic text-[#7C8A82]">
                      {fa}
                    </i>
                  ))}
                  {focusAreas.length > 3 ? (
                    <i className="rounded bg-[#F8F2E2] px-1 text-[9px] not-italic text-[#7C8A82]">
                      +{focusAreas.length - 3}
                    </i>
                  ) : null}
                </span>
              )}
              {errorByKey[id] ? (
                <span className="mt-1 block text-[10px] font-semibold text-[#9F1239]">{errorByKey[id]}</span>
              ) : null}
            </button>
          );
        })}
      </div>
      )}

      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#2A1F17]">
        Add a new department to the master
      </p>
      <div className="flex flex-wrap items-center gap-2 rounded-[10px] border border-dashed border-[rgba(201,169,97,0.55)] bg-[rgba(201,169,97,0.06)] px-3 py-2.5">
        <input
          aria-label="Custom department name"
          className="min-w-[180px] flex-1 rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2.5 py-1.5 text-[12px] text-[#0B3B2E] outline-none focus:border-[#0B3B2E] focus:ring-[3px] focus:ring-[rgba(166,206,57,0.22)]"
          placeholder="Department name e.g. Land & Liaison"
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          disabled={creating || addingToMaster}
        />
        <input
          aria-label="Custom focus areas"
          className="min-w-[180px] flex-[1.4] rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2.5 py-1.5 text-[12px] text-[#0B3B2E] outline-none focus:border-[#0B3B2E] focus:ring-[3px] focus:ring-[rgba(166,206,57,0.22)]"
          placeholder="Focus areas (comma separated)"
          value={customFocus}
          onChange={(e) => setCustomFocus(e.target.value)}
          disabled={creating || addingToMaster}
        />
        <button
          type="button"
          disabled={!customName.trim() || addingToMaster || creating}
          onClick={() => void handleAddToMaster()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 py-1.5 text-[11.5px] font-semibold text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[#F8F2E2] disabled:opacity-50"
        >
          {addingToMaster ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Add to master
        </button>
      </div>
      <p className="mt-1.5 text-[11px] text-[#7C8A82]">
        New departments are saved to the global master so every project uses the same name.
      </p>
        </>
      ) : (
        <div className="space-y-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#2A1F17]">
            Assign department heads · {createdDepts.length} new department{createdDepts.length === 1 ? '' : 's'}
          </p>
          <p className="text-[11.5px] text-[#7C8A82]">
            Optional — each head reports to{' '}
            {approver ? (
              <b className="text-[#2A1F17]">
                {approver.userName ?? approver.userId} · {ROLE_LABEL[approver.role]}
              </b>
            ) : (
              <b className="text-[#8A5A08]">no governance member yet — add a PMO or Project Head first</b>
            )}
            . You can also assign heads later from the diagram.
          </p>
          {createdDepts.map((dept) => {
            const row = assignByOrgId[dept.orgUnitId] ?? { userId: '', status: 'idle' as const };
            return (
              <div key={dept.orgUnitId} className="rounded-[10px] border border-[rgba(11,59,46,0.18)] bg-[rgba(255,254,251,0.96)] p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[12px] font-bold text-[#0B3B2E]">{dept.name}</p>
                  {row.status === 'done' ? (
                    <span className="inline-flex items-center gap-1 rounded bg-[rgba(51,146,116,0.14)] px-1.5 py-0.5 text-[10px] font-bold text-[#1F5A47]">
                      <Check className="h-3 w-3" /> Head assigned
                    </span>
                  ) : row.status === 'skipped' ? (
                    <span className="rounded bg-[#F8F2E2] px-1.5 py-0.5 text-[10px] font-bold text-[#7C8A82]">
                      Skipped
                    </span>
                  ) : null}
                </div>
                {row.status !== 'done' && row.status !== 'skipped' ? (
                  <div className="mt-2 space-y-2">
                    <EpcTeamUserPicker
                      users={headCandidates.users}
                      value={row.userId}
                      onChange={(uid) =>
                        setAssignByOrgId((prev) => ({
                          ...prev,
                          [dept.orgUnitId]: { userId: uid, status: 'idle' },
                        }))
                      }
                      disabled={row.status === 'saving'}
                      roleLabelByUserId={headCandidates.roleLabelByUserId}
                      placeholder="Search EPC department heads…"
                      emptyHint="No EPC department heads found. Assign the role in EPC from User Management."
                    />
                    {row.status === 'error' && row.error ? (
                      <p className="text-[11px] font-semibold text-[#9F1239]">{row.error}</p>
                    ) : null}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={!row.userId || !approver || row.status === 'saving'}
                        onClick={() => void assignHead(dept)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[rgba(11,59,46,0.45)] bg-[#264f3c] px-3 py-1.5 text-[11.5px] font-semibold text-white shadow-[0_8px_18px_-12px_rgba(38,79,60,0.5),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#2d5c47] disabled:opacity-50"
                      >
                        {row.status === 'saving' ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <UserCheck className="h-3.5 w-3.5" />
                        )}
                        Assign head
                      </button>
                      <button
                        type="button"
                        disabled={row.status === 'saving'}
                        onClick={() => skipDept(dept)}
                        className="rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-3 py-1.5 text-[11.5px] font-semibold text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)]"
                      >
                        Skip
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </RightDrawer>
  );
}
